<script>
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
import 'swiper/css/swiper.css';
import { BBadge } from 'bootstrap-vue';
import EstoqueBadge from '@/components/EstoqueBadge.vue';
import SLSEstoqueService from '@/services/SistemaDKP/SLSEstoque.service';
import { isPerfilClienteAtivo } from '@/libs/perfilCliente';
import { ocultarEstoqueCertificacao, tipoPedidoSelecionadoParaEstoque } from '@/utils/estoqueVisibilidade';

export default {
  name: 'ProductionItem',
  props: ['produto', 'estoqueInfo', 'preco'],
  components: {
    Swiper,
    SwiperSlide,
    BBadge,
    EstoqueBadge,
  },
  computed: {
    ocultarEstoque() {
      return ocultarEstoqueCertificacao(tipoPedidoSelecionadoParaEstoque(this.$store));
    },
    estoqueFormatado() {
      return SLSEstoqueService.formatarEstoque(this.estoqueInfo || null);
    },
    isClientePerfil() {
      return isPerfilClienteAtivo();
    },
    orderedImages() {
      if (!this.produto?.imagens?.length) {
        return [];
      }

      const principalIndex = this.produto.imagens.findIndex(
        (img) => img.caminho === this.produto.caminho,
      );

      if (principalIndex <= 0) {
        return this.produto.imagens;
      }

      const images = this.produto.imagens.slice();
      const [principalImage] = images.splice(principalIndex, 1);
      images.unshift(principalImage);
      return images;
    },
    hasMultipleImages() {
      return this.orderedImages.length > 1;
    },
    itemId() {
      return this.produto?.item_id ?? this.produto?.itemId ?? this.produto?.CodItem ?? '';
    },
    itemDescricao() {
      return this.produto?.item_descricao ?? this.produto?.itemDescricao
        ?? this.produto?.Descricao ?? this.produto?.item_name ?? '';
    },
    swiperOptions() {
      return {
        loop: this.hasMultipleImages,
        loopedSlides: this.hasMultipleImages ? 5 : 0,
        spaceBetween: 10,
        slidesPerView: 1,
        centeredSlides: true,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
      };
    },
    swiperOptionThumbs() {
      return {
        loop: this.hasMultipleImages,
        loopedSlides: this.hasMultipleImages ? 5 : 0,
        spaceBetween: 10,
        centeredSlides: true,
        slidesPerView: Math.min(4, this.orderedImages.length),
        touchRatio: 0.2,
        slideToClickedSlide: true,
      };
    },
  },
  data() {
    return {
      readyToShowSwiper: false,
    };
  },
  methods: {
    onCheckItem() {
      // A Home card represents one exact SKU.  Emit on every click so a user
      // can reopen the same SKU after closing the modal.
      this.$store.commit('InfoPedido/SET_ITEM_SELECTED', this.produto);
      this.$root.$emit('infoPedido:checkGrade');
    },
  },
  mounted() {
    setTimeout(() => {
      this.readyToShowSwiper = true;
      this.$nextTick(() => {
        if (this.$refs.refSwiperTop && this.$refs.swiperThumbs) {
          const refSwiperTop = this.$refs.refSwiperTop.$swiper;
          const swiperThumbs = this.$refs.swiperThumbs.$swiper;
          refSwiperTop.controller.control = swiperThumbs;
          swiperThumbs.controller.control = refSwiperTop;
        }
      });
    }, 10);
  },
};
</script>

<template>
  <b-card no-body class="b-card-premium" style="border: none;">

    <swiper
        v-if="readyToShowSwiper"
        ref="refSwiperTop"
        class="swiper-gallery gallery-top cursor-pointer"
        :options="swiperOptions"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
        :navigation="true"
        @click="onCheckItem"
    >
      <swiper-slide
          v-for="(data,index) in orderedImages"
          :key="index"
          class="swiper-slide-top"

      >
        <b-img
            :src="data.caminho"
            fluid
            loading="lazy"
            class="swiper-image principal"
        />
      </swiper-slide>
    </swiper>
    <div
        class="text-bold cursor-pointer"
        @click="onCheckItem"
    >
      <h4
          id="description-item"
          class="font-weight-bold text-center my-1"
          style="font-size: 1.0rem"
      >{{ itemId }} - {{ itemDescricao }}</h4>
      <div v-if="!isClientePerfil && !ocultarEstoque" class="text-center mb-1">
        <estoque-badge v-if="estoqueInfo !== undefined && $store.getters['preferencias/controleEstoqueAtivo']" :estoque-info="estoqueInfo" />
        <b-badge v-else variant="secondary" style="font-size:0.7rem">
          <feather-icon icon="PackageIcon" size="10" class="mr-25" />—
        </b-badge>
      </div>
      <div v-if="preco > 0" class="text-center font-weight-bold mb-1">
        {{ Number(preco).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}
      </div>
    </div>
    <swiper
        v-if="hasMultipleImages"
        ref="swiperThumbs"
        class="swiper gallery-thumbs my-1"
        :options="swiperOptionThumbs"
    >
      <swiper-slide
          v-for="(data,index) in orderedImages"
          :key="index"
          class="swiper-slide-thumbs cursor-pointer"
      >
        <b-img
            :src="data.caminho"
            fluid
            loading="lazy"
            class="swiper-image"
        />
      </swiper-slide>
    </swiper>
  </b-card>
</template>

<style scoped lang="scss">
.b-card-premium {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  background: var(--sf-card-bg, #ffffff);
  backdrop-filter: blur(8px);
  border: 1px solid var(--sf-accent-light, rgba(37, 99, 235, 0.15)) !important;
  border-radius: 12px;
  box-shadow: var(--sf-shadow-sm);
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s ease, border-color 0.3s ease;
  overflow: hidden;
}
.b-card-premium ::v-deep .swiper-container,
.b-card-premium ::v-deep .swiper {
  width: 100%;
  max-width: 100%;
}
.b-card-premium:hover {
  transform: translateY(-4px);
  box-shadow: var(--sf-shadow-lg);
  border-color: var(--sf-accent-bg-hover, rgba(37, 99, 235, 0.3)) !important;
}

body.dark-layout .b-card-premium {
  background: var(--sf-card-bg) !important;
  border-color: var(--sf-accent-light) !important;
  box-shadow: var(--sf-shadow-md);
}
body.dark-layout .b-card-premium:hover {
  background: var(--sf-surface-secondary) !important;
  border-color: var(--sf-accent-bg-hover) !important;
  box-shadow: var(--sf-shadow-lg);
}

.swiper-slide-top {
  border-radius: 10px;
  overflow: hidden;
  background: var(--sf-surface-primary, #ffffff);
}
body.dark-layout .swiper-slide-top {
  background: var(--sf-surface-secondary);
}

.swiper-slide-thumbs {
  border: 2px solid transparent;
  transition: all 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
}

#description-item {
  color: var(--sf-text-primary);
  font-weight: 700;
  font-size: 1.05rem;
  letter-spacing: -0.01em;
  transition: color 0.2s;
  padding: 0.5rem;
}
#description-item:hover { color: var(--primary, var(--sf-accent)); }
body.dark-layout #description-item { color: var(--sf-text-primary); }
body.dark-layout #description-item:hover { color: var(--primary, var(--sf-accent)); }

.swiper-slide-thumbs.selected { border-color: var(--primary, var(--sf-accent)); }

.swiper-image { width: 100%; object-fit: contain; height: 65px; background: var(--sf-surface-primary, #fff); }

.swiper-image.principal {
  height: 100%; width: 100%; object-fit: contain; max-height: 280px; background: var(--sf-surface-primary, #fff); transition: transform 0.4s ease, opacity 0.4s ease, filter 0.4s ease;
}
.swiper-image.principal:hover { opacity: 0.95; transform: scale(1.08); filter: brightness(1.05); }

.swiper-slide-thumbs.swiper-slide:not(.swiper-slide-active) { opacity: 0.4; transition: opacity 0.3s ease; transform: scale(0.95); }
.swiper-slide-thumbs.swiper-slide-active { border: 2px solid var(--primary, var(--sf-accent)); opacity: 1; transform: scale(1); }

.badge {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 5px 10px;
  font-size: 0.8rem;
  z-index: 999;
}
</style>
